import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { TextProps } from '@chakra-ui/react';
import { Flex, Text, Tooltip } from '@chakra-ui/react';

import { CustomIcon } from '../../icon';
import { useNabanStoreSelectedAuthenticationMethod } from '../store';

type Props = {
  onClick: VoidFunction;
  labelProps?: TextProps;
  withTooltip?: boolean;
};

export const AuthenticationMethodSelectorButton = ({
  labelProps,
  withTooltip = true,
  onClick,
}: Props) => {
  const { t } = useTranslation('naban');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const selectedAuthenticationMethod = useNabanStoreSelectedAuthenticationMethod();

  const title = useMemo(() => {
    switch (selectedAuthenticationMethod?.type) {
      case 'company':
        return t('authenticationMethods.selector.options.company', {
          name: selectedAuthenticationMethod.name,
        });
      case 'companyAndSisterCompanies':
        return t('authenticationMethods.selector.options.subsidiaryCompanies', {
          name: selectedAuthenticationMethod.name,
        });
      case 'heir':
        return t('authenticationMethods.selector.options.heir', {
          name: `${selectedAuthenticationMethod.firstName} ${selectedAuthenticationMethod.lastName}`,
        });
      case 'personal':
        return t('authenticationMethods.selector.options.personal');
      default:
        return '';
    }
  }, [selectedAuthenticationMethod, t]);

  const onTriggerButtonClick = useCallback(() => {
    onClick();
    // The focus state applied on click triggers the tooltip to be shown.
    buttonRef.current?.blur();
  }, [onClick]);

  if (selectedAuthenticationMethod == null) {
    return null;
  }

  return (
    <Tooltip
      label={t('authenticationMethods.selector.tooltip.label')}
      fontSize='lg'
      hasArrow
      borderRadius='lg'
      isDisabled={!withTooltip}
      p={4}
      openDelay={1000}
      lineHeight='110%'>
      <Flex
        ref={buttonRef}
        as='button'
        alignItems='center'
        gap={2}
        aria-label={t('authenticationMethods.selector.button.ariaLabel')}
        onClick={onTriggerButtonClick}
        width='fit-content'>
        <Text
          variant='heading3'
          fontSize={{ base: '28px', md: '24px' }}
          textAlign='left'
          textOverflow='ellipsis'
          noOfLines={1}
          {...labelProps}>
          {title}
        </Text>
        <Flex
          rounded='full'
          backgroundColor='neutral100'
          h={8}
          w={8}
          alignItems='center'
          justifyContent='center'
          flexShrink={0}>
          <CustomIcon name='chevron-down' color='neutral600' size={18} />
        </Flex>
      </Flex>
    </Tooltip>
  );
};
